import { useCallback, useEffect, useRef } from 'react';
import { Box, Code, Link, Text, OrderedList, UnorderedList, ListItem } from '@chakra-ui/react';
import type { TextBlock as TextBlockType, ProseMirrorDoc, ProseMirrorNode, TypeScaleToken } from '../../editor/types';
import { useEditorBlockContext } from '../EditorBlockContext';
import { InlineTextEditor } from '../../editor/components/InlineTextEditor';
import {
  TYPE_SCALE,
  FONT_PAIRS,
  LINE_HEIGHT_VALUES,
  LETTER_SPACING_VALUES,
  getFontFamilyById,
} from '../../editor/constants/typographyPresets';
import { loadFontPair, loadFontById } from '../utils/fontLoader';
import { useEffectiveTypography } from '../SectionTypographyContext';
import { usePreviewDevice } from '../PreviewDeviceContext';

type TextBlockProps = TextBlockType['props'] & { blockId?: string };

interface Mark {
  type: string;
  attrs?: Record<string, unknown>;
}

function renderMarks(text: string, marks: Mark[]): React.ReactNode {
  let node: React.ReactNode = text;

  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        node = <strong>{node}</strong>;
        break;
      case 'italic':
        node = <em>{node}</em>;
        break;
      case 'underline':
        node = <u>{node}</u>;
        break;
      case 'strike':
        node = <s>{node}</s>;
        break;
      case 'code':
        node = <Code>{node}</Code>;
        break;
      case 'link':
        node = (
          <Link href={mark.attrs?.href as string} target="_blank" color="blaze.blaze" textDecoration="underline">
            {node}
          </Link>
        );
        break;
      case 'textStyle': {
        const style: React.CSSProperties = {};
        if (mark.attrs?.color) style.color = mark.attrs.color as string;
        if (mark.attrs?.fontFamily) style.fontFamily = mark.attrs.fontFamily as string;
        node = <span style={style}>{node}</span>;
        break;
      }
    }
  }

  return node;
}

function renderNode(node: ProseMirrorNode, index: number): React.ReactNode {
  const key = `${node.type}-${index}`;

  switch (node.type) {
    case 'doc':
      return <>{node.content?.map((child, i) => renderNode(child, i))}</>;

    case 'paragraph':
      return (
        <Text key={key} color="dune.dune" mb={2}>
          {node.content?.map((child, i) => renderNode(child, i))}
        </Text>
      );

    case 'text':
      if (!node.text) return null;
      if (node.marks && node.marks.length > 0) {
        return <span key={key}>{renderMarks(node.text, node.marks)}</span>;
      }
      return <span key={key}>{node.text}</span>;

    case 'heading': {
      const level = (node.attrs?.level as number) ?? 2;
      const tag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      return (
        <Text key={key} as={tag} fontSize={level === 1 ? '4xl' : level === 2 ? '3xl' : level === 3 ? '2xl' : 'xl'} color="dune.dune" mb={2} fontWeight="bold">
          {node.content?.map((child, i) => renderNode(child, i))}
        </Text>
      );
    }

    case 'bulletList':
    case 'bullet_list':
      return (
        <UnorderedList key={key} color="dune.dune" mb={2} pl={4}>
          {node.content?.map((child, i) => renderNode(child, i))}
        </UnorderedList>
      );

    case 'orderedList':
    case 'ordered_list':
      return (
        <OrderedList key={key} color="dune.dune" mb={2} pl={4}>
          {node.content?.map((child, i) => renderNode(child, i))}
        </OrderedList>
      );

    case 'listItem':
    case 'list_item':
      return (
        <ListItem key={key}>
          {node.content?.map((child, i) => renderNode(child, i))}
        </ListItem>
      );

    case 'blockquote':
      return (
        <Box key={key} as="blockquote" borderLeft="3px solid" borderColor="blaze.500" pl={4} my={2} color="dune.dune" fontStyle="italic">
          {node.content?.map((child, i) => renderNode(child, i))}
        </Box>
      );

    case 'codeBlock':
    case 'code_block':
      return (
        <Box key={key} as="pre" bg="whiteAlpha.100" p={4} borderRadius="md" mb={2} overflowX="auto">
          <Code display="block" whiteSpace="pre-wrap" color="dune.dune">
            {node.content?.map((child, i) => renderNode(child, i))}
          </Code>
        </Box>
      );

    case 'hardBreak':
    case 'hard_break':
      return <br key={key} />;

    case 'horizontalRule':
    case 'horizontal_rule':
      return <Box key={key} as="hr" borderTop="1px solid" borderColor="whiteAlpha.200" my={4} />;

    default:
      return null;
  }
}

export function TextBlock({ content, alignment: _alignment, max_width, typography: blockTypography, mobile_size_token, blockId }: TextBlockProps): React.ReactElement {
  const ctx = useEditorBlockContext();
  const isInEditor = ctx?.isEditorCanvas === true && blockId !== undefined;
  const previewDevice = usePreviewDevice();

  // Merge with section-level typography (block overrides section)
  const typography = useEffectiveTypography(blockTypography);

  // Load font pair if specified
  useEffect(() => {
    if (typography?.font_pair) {
      loadFontPair(typography.font_pair);
    }
  }, [typography?.font_pair]);

  // Track latest doc in a ref — save only on blur to avoid re-render cycles
  const pendingDocRef = useRef<ProseMirrorDoc | null>(null);

  const handleChange = useCallback(
    (doc: ProseMirrorDoc) => {
      pendingDocRef.current = doc;
    },
    [],
  );

  const handleBlur = useCallback(() => {
    if (pendingDocRef.current && ctx?.onBlockPropsChange && blockId) {
      ctx.onBlockPropsChange(ctx.sectionId, blockId, { content: pendingDocRef.current });
      pendingDocRef.current = null;
    }

  }, [ctx, blockId]);

  if (!content) return <></>;

  // Resolve effective size token based on device context
  const desktopSizeToken = typography?.size_token;
  const effectiveSizeToken: TypeScaleToken | undefined =
    (previewDevice === 'mobile' && mobile_size_token) ? mobile_size_token : desktopSizeToken;

  // Build typography styles
  const typoStyle: React.CSSProperties = {};
  if (effectiveSizeToken && TYPE_SCALE[effectiveSizeToken]) {
    typoStyle.fontSize = `${TYPE_SCALE[effectiveSizeToken].px}px`;
  }
  if (typography?.font_pair) {
    const pair = FONT_PAIRS.find((p) => p.id === typography.font_pair);
    if (pair) typoStyle.fontFamily = pair.body;
  }
  // Per-block font_family override takes precedence over font_pair
  if (typography?.font_family) {
    const resolvedFamily = getFontFamilyById(typography.font_family);
    if (resolvedFamily) {
      typoStyle.fontFamily = resolvedFamily;
      loadFontById(typography.font_family);
    }
  }
  if (typography?.line_height && LINE_HEIGHT_VALUES[typography.line_height] !== undefined) {
    typoStyle.lineHeight = LINE_HEIGHT_VALUES[typography.line_height];
  }
  if (typography?.letter_spacing && LETTER_SPACING_VALUES[typography.letter_spacing] !== undefined) {
    typoStyle.letterSpacing = LETTER_SPACING_VALUES[typography.letter_spacing];
  }

  // Production: media query for mobile_size_token when not in editor preview
  const mobileSx = (!previewDevice && mobile_size_token && TYPE_SCALE[mobile_size_token])
    ? { '@media (max-width: 768px)': { fontSize: `${TYPE_SCALE[mobile_size_token].px}px` } }
    : undefined;

  if (isInEditor) {
    const editRendered = (
      <Box gc-testing-id="blockTextEditing" color="dune.dune" style={typoStyle} sx={mobileSx} onClick={(e: React.MouseEvent) => { e.stopPropagation(); ctx?.onSelectBlock(ctx.sectionId, blockId!); }}>
        <InlineTextEditor
          content={content}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter text..."
        />
      </Box>
    );
    if (max_width) {
      return <Box maxW={max_width}>{editRendered}</Box>;
    }
    return editRendered;
  }

  const rendered = (
    <Box gc-testing-id="blockText" color="dune.dune" style={typoStyle} sx={mobileSx}>
      {content.content?.map((node, i) => renderNode(node, i))}
    </Box>
  );

  if (max_width) {
    return <Box maxW={max_width}>{rendered}</Box>;
  }

  return rendered;
}
